import React, { FC, useCallback, useState } from 'react';

import { useRouter } from 'expo-router';
import { refetchLoyaltyUserSafely } from 'features/loyalty-user/refetch-loyalty-user-safely';
import { View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import { PullToReloadScrollView } from 'components/pull-to-reload/pull-to-reload';
import { QuestActivationSource, QuestSection } from 'components/quests';
import { LoyaltyNotEnrolled } from 'pages/loyalty/loyalty-not-enrolled';
import { LoyaltyRewardsExpiringPoints } from 'pages/loyalty/loyalty-rewards/loyalty-rewards-expiring-points';
import { LoyaltyRewardsSkeletonLoader } from 'pages/loyalty/loyalty-rewards/loyalty-rewards-skeleton-loader';
import { LoyaltyRewardsListContainer } from 'pages/loyalty/loyalty-rewards/loyalty-rewards.styled';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { AllowedCelebrationTypes } from 'state/loyalty/hooks/types';
import { useLoyaltyCelebrations } from 'state/loyalty/hooks/use-loyalty-celebrations';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/trace';
import { waitForDuration } from 'utils/wait-for-duration';

import { logRewardSelected } from '../analytics/reward-selected';
import { useEnableServiceRewardsBrowse, useServiceRewards } from '../state/service-rewards-context';
import { Reward } from '../types';
import { getLoyaltyServiceRewardDetailHref } from '../utils/routing';

import { ServiceRewardsList } from './service-rewards-list';

export const RewardsBase: FC = () => {
  useEnableServiceRewardsBrowse();
  const { user, loading: userDataIsLoading, isAuthenticated } = useAuthContext();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const { loading: loyaltyUserLoading, loyaltyUser } = useLoyaltyUser();

  const { rewardCategories, loading: rewardDataIsLoading, refreshRewards } = useServiceRewards();

  const enableLoyaltyCelebrations = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_CELEBRATIONS);

  const { recheckCelebrations } = useLoyaltyCelebrations({
    allowedCelebrationTypes: [
      AllowedCelebrationTypes.CHALLENGE_COMPLETED,
      AllowedCelebrationTypes.COMBINED_CHALLENGE_COMPLETED,
      AllowedCelebrationTypes.PROFILE_CHALLENGE_COMPLETED,
    ],
    disableCelebrations: !enableLoyaltyCelebrations,
  });

  const [isForceRefreshing, setForceRefresh] = useState<boolean>(false);

  const router = useRouter();

  const loyaltyId = user?.loyaltyId;

  // Refetch for updated crowns when navigating to page
  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const handleRewardOnPress = useCallback(
    (reward: Reward) => {
      startTrace(TraceName.REWARD_DETAIL_LOAD, { id: reward.id, name: reward.name });
      logRewardSelected(reward);

      router.navigate(
        getLoyaltyServiceRewardDetailHref({
          loyaltyEngineId: reward.id,
          rewardId: reward.cmsId || reward.id,
        })
      );
    },
    [router]
  );

  const forceRefresh = async () => {
    await waitForDuration(1500);
    if (loyaltyId) {
      refetchLoyaltyUserSafely(refetchLoyaltyUser, 'rewards pull-to-refresh');
      setForceRefresh(true);
      await refreshRewards();
      setForceRefresh(false);
      recheckCelebrations();
    }
  };

  const retrievingUser = !loyaltyUser && loyaltyUserLoading;

  if (userDataIsLoading || retrievingUser || rewardDataIsLoading || isForceRefreshing) {
    return <LoyaltyRewardsSkeletonLoader />;
  }

  if (isAuthenticated && !loyaltyId) {
    return <LoyaltyNotEnrolled />;
  }

  return (
    <PullToReloadScrollView
      progressViewOffset={10}
      onRefresh={forceRefresh}
      contentContainerStyle={centeredRowContainerStyles.container}
      isDisabled={!isAuthenticated}
    >
      <View style={centeredRowContainerStyles.content}>
        <LoyaltyRewardsExpiringPoints />
        <QuestSection showHeader={false} activationSource={QuestActivationSource.REWARDS_REDEEM} />
        <LoyaltyRewardsListContainer>
          <ServiceRewardsList categories={rewardCategories} handleOnPress={handleRewardOnPress} />
        </LoyaltyRewardsListContainer>
      </View>
    </PullToReloadScrollView>
  );
};
